import React from 'react';
import { useWizard } from '../../context/WizardContext';
import { ArrowLeft, ArrowRight, Link2, Lightbulb, MessageSquare, BarChart3, AlertCircle } from 'lucide-react';

const B5_WizardFIntegration = () => {
  const { dataB, nextStep, prevStep } = useWizard();

  const qualMethods = dataB?.qualitativeMethods || {};
  const quantMethods = dataB?.quantitativeMethods || {};

  const hasQualMethods = Object.values(qualMethods).some(v => v);
  const hasQuantMethods = quantMethods.descriptiveStats?.enabled || quantMethods.inferentialTests?.enabled;

  const selectedQualMethods = Object.entries(qualMethods)
    .filter(([k, v]) => v)
    .map(([k]) => k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim());

  const selectedQuantCategories = [];
  if (quantMethods.descriptiveStats?.enabled) {
    selectedQuantCategories.push('Descriptive Statistics');
  }
  if (quantMethods.inferentialTests?.enabled) {
    selectedQuantCategories.push('Inferential Tests');
  }

  const handleNext = () => {
    nextStep();
  };

  return (
    <div className="wizard-step">
      <h2>Step 6: Survey Integration</h2>
      <p className="step-description">
        Your analysis plan is ready. Now you can build your survey in Wizard F with the right question types.
      </p>

      <div style={{
        background: 'linear-gradient(135deg, #f0f9ff 0%, #ecfeff 50%, #f0fdf4 100%)',
        border: '1px solid #67e8f9',
        borderRadius: '16px',
        padding: '2rem',
        marginBottom: '2rem',
        textAlign: 'center'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #0891b2 0%, #059669 100%)',
          marginBottom: '1rem'
        }}>
          <Link2 size={32} color="white" />
        </div>
        
        <h3 style={{ margin: '0 0 0.5rem 0', color: '#1e293b' }}>Analysis Plan Complete!</h3>
        <p style={{ margin: '0 0 1rem 0', color: '#475569', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' }}>
          Your data analysis selections will appear as helpful reminders when you build your 
          survey in Wizard F, guiding you to include the right question types.
        </p>

        <div style={{
          background: '#fef3c7',
          border: '1px solid #fcd34d',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          maxWidth: '500px'
        }}>
          <AlertCircle size={18} color="#92400e" style={{ flexShrink: 0 }} />
          <span style={{ color: '#78350f', fontSize: '0.9rem', textAlign: 'left' }}>
            Your choices here will be reflected in Wizard F when you build your questionnaire. 
            Continue through the remaining wizards first.
          </span>
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Your Analysis Plan Summary</h3>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1rem'
        }}>
          {hasQualMethods && (
            <div style={{
              background: '#f5f3ff',
              border: '1px solid #c4b5fd',
              borderRadius: '12px',
              padding: '1.25rem'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                marginBottom: '0.75rem'
              }}>
                <MessageSquare size={20} color="#7c3aed" />
                <h4 style={{ margin: 0, color: '#7c3aed' }}>Qualitative Methods</h4>
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#475569' }}>
                {selectedQualMethods.map((method, idx) => (
                  <li key={idx} style={{ marginBottom: '0.25rem' }}>{method}</li>
                ))}
              </ul>
            </div>
          )}

          {hasQuantMethods && (
            <div style={{
              background: '#ecfeff',
              border: '1px solid #67e8f9',
              borderRadius: '12px',
              padding: '1.25rem'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                marginBottom: '0.75rem'
              }}>
                <BarChart3 size={20} color="#0891b2" />
                <h4 style={{ margin: 0, color: '#0891b2' }}>Quantitative Methods</h4>
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#475569' }}>
                {selectedQuantCategories.map((cat, idx) => (
                  <li key={idx} style={{ marginBottom: '0.25rem' }}>{cat}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div style={{
        background: '#fefce8',
        border: '1px solid #fde047',
        borderRadius: '12px',
        padding: '1.25rem',
        marginBottom: '2rem'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: '0.75rem'
        }}>
          <Lightbulb size={20} color="#ca8a04" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#854d0e' }}>What Happens Next</h4>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '1.25rem', 
              color: '#713f12',
              fontSize: '0.9rem'
            }}>
              <li style={{ marginBottom: '0.5rem' }}>
                When you open Wizard F, you will see a reminder panel showing your analysis plan
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                As you add questions, the panel will suggest appropriate question types
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                After collecting responses, use the Analytics panel to run your planned analyses
              </li>
              <li>
                You can always return here to modify your analysis plan
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div style={{
        background: '#f8fafc',
        borderRadius: '12px',
        padding: '1.25rem',
        marginBottom: '2rem'
      }}>
        <h4 style={{ margin: '0 0 0.75rem 0', color: '#1e293b' }}>Alternative: Continue to Review</h4>
        <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
          You can also continue to the next step to review your complete analysis plan and export 
          a Data Analysis Report before building your survey.
        </p>
      </div>

      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={prevStep}>
          <ArrowLeft size={18} />
          Back
        </button>
        <button className="btn btn-primary" onClick={handleNext}>
          Continue to Review
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default B5_WizardFIntegration;
